
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { BackgroundGradientAnimation } from "@/components/ui/background-gradient-animation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from "@/components/ui/select";

export default function Onboarding() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("+61"); // Default to Australia
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    specialization: "",
  });

  const getPlaceholder = (code: string) => {
    switch (code) {
      case "+61": return "0412 345 678";  // Australia
      case "+64": return "021 234 5678";  // New Zealand
      case "+1": return "(555) 123-4567";  // USA
      case "+44": return "07700 900123";  // UK
      default: return "Enter phone number";
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove any non-digit characters
    const cleaned = value.replace(/\D/g, "");
    
    // Format based on country code
    if (countryCode === "+61" && cleaned.length <= 9) {
      // Australian format: 0XXX XXX XXX
      const match = cleaned.match(/^(\d{0,4})(\d{0,3})(\d{0,3})$/);
      if (match) {
        return match
          .slice(1)
          .filter(Boolean)
          .join(" ");
      }
    }
    return cleaned;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth');
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: formData.fullName,
          phone: `${countryCode}${formData.phone.replace(/\s/g, '')}`,
          specialization: formData.specialization,
        })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: "Profile updated successfully!",
        description: "Welcome to the platform.",
      });

      navigate('/');
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BackgroundGradientAnimation>
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md backdrop-blur-lg bg-white/10 border-white/20">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center text-white">
              Complete Your Profile
            </CardTitle>
            <CardDescription className="text-center text-white/70">
              Tell us more about yourself to get started
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-white text-sm font-medium">
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, fullName: e.target.value }))
                  }
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 transition-colors focus:border-white/40"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white text-sm font-medium">
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <Select 
                    value={countryCode}
                    onValueChange={setCountryCode}
                  >
                    <SelectTrigger className="w-[110px] bg-white/10 border-white/20 text-white">
                      <SelectValue placeholder="Code" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="+61">🇦🇺 +61</SelectItem>
                      <SelectItem value="+64">🇳🇿 +64</SelectItem>
                      <SelectItem value="+1">🇺🇸 +1</SelectItem>
                      <SelectItem value="+44">🇬🇧 +44</SelectItem>
                      <SelectSeparator className="my-2" />
                      <SelectItem value="+33">🇫🇷 +33</SelectItem>
                      <SelectItem value="+49">🇩🇪 +49</SelectItem>
                      <SelectItem value="+81">🇯🇵 +81</SelectItem>
                      <SelectItem value="+82">🇰🇷 +82</SelectItem>
                      <SelectItem value="+86">🇨🇳 +86</SelectItem>
                      <SelectItem value="+91">🇮🇳 +91</SelectItem>
                      <SelectItem value="+351">🇵🇹 +351</SelectItem>
                      <SelectItem value="+353">🇮🇪 +353</SelectItem>
                      <SelectItem value="+39">🇮🇹 +39</SelectItem>
                      <SelectItem value="+34">🇪🇸 +34</SelectItem>
                      <SelectItem value="+52">🇲🇽 +52</SelectItem>
                      <SelectItem value="+55">🇧🇷 +55</SelectItem>
                      <SelectItem value="+7">🇷🇺 +7</SelectItem>
                      <SelectItem value="+46">🇸🇪 +46</SelectItem>
                      <SelectItem value="+41">🇨🇭 +41</SelectItem>
                      <SelectItem value="+31">🇳🇱 +31</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={getPlaceholder(countryCode)}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    required
                    className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 transition-colors focus:border-white/40"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="specialisation" className="text-white text-sm font-medium">
                  Specialisation
                </Label>
                <Input
                  id="specialisation"
                  placeholder="e.g. Strength & Conditioning, Nutrition, etc."
                  value={formData.specialization}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, specialization: e.target.value }))
                  }
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 transition-colors focus:border-white/40"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Complete Profile"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </BackgroundGradientAnimation>
  );
}
